import React, { useState, useCallback, useEffect } from 'react';
import ReactFlow, { 
  MiniMap, 
  Controls, 
  Background, 
  ReactFlowProvider,
  applyNodeChanges,
  applyEdgeChanges
} from 'reactflow';
import 'reactflow/dist/style.css';
import { themeStyles, customStyles } from './ArtemisStyles';
import { nodeTypes } from './ArtemisCustomNodes';

// Security Hardening nodes - showing evolution of security measures
const getInitialNodes = () => {
  const savedPositions = localStorage.getItem('artemisSecurityHardeningPositions');
  if (savedPositions) {
    return JSON.parse(savedPositions);
  }
  
  return [
    // User Intent
    {
      id: 'user-intent',
      data: { label: '💬 "Harden this API against security vulnerabilities"' },
      position: { x: 400, y: 50 },
      style: themeStyles.problemNode,
    },
    
    // Security Analysis
    {
      id: 'security-analysis',
      data: { label: '🔍 Security Scan\n• 12 Critical vulnerabilities\n• 8 SQL injection points\n• No rate limiting\n• Weak authentication' },
      position: { x: 800, y: 50 },
      style: { ...themeStyles.analysisNode, width: 220 },
    },
    
    // Initial Vulnerable Code
    {
      id: 'initial-code',
      type: 'collapsibleCode',
      data: { 
        filename: 'vulnerable-api.js',
        code: '// Vulnerable user authentication\n' +
              'app.post("/login", (req, res) => {\n' +
              '  const { username, password } = req.body;\n' +
              '  \n' +
              '  // SQL Injection vulnerability\n' +
              '  const query = `SELECT * FROM users WHERE \n' +
              '    username = \'${username}\' AND \n' +
              '    password = \'${password}\'`;\n' +
              '  \n' +
              '  db.query(query, (err, results) => {\n' +
              '    if (results.length > 0) {\n' +
              '      // No session management\n' +
              '      res.json({ success: true, user: results[0] });\n' +
              '    } else {\n' +
              '      res.json({ success: false });\n' +
              '    }\n' +
              '  });\n' +
              '});',
        issues: 'SQL injection, no password hashing, exposed user data, no rate limiting',
        score: 1.0
      },
      position: { x: 400, y: 200 },
    },
    
    // Scoring System
    {
      id: 'scoring-label',
      data: { label: '🛡️ Security Assessment Metrics' },
      position: { x: 1100, y: 200 },
      style: { ...themeStyles.metricNode, width: 240 },
    },
    {
      id: 'vulnerability-score',
      data: { label: '🚨 Vulnerabilities\nCritical: 12\nHigh: 8' },
      position: { x: 950, y: 280 },
      style: themeStyles.metricNode,
    },
    {
      id: 'authentication-score',
      data: { label: '🔐 Auth Security\nScore: 1/10\nWeak mechanisms' },
      position: { x: 1100, y: 280 },
      style: themeStyles.metricNode,
    },
    {
      id: 'data-protection-score',
      data: { label: '🔒 Data Protection\nScore: 2/10\nNo encryption' },
      position: { x: 1250, y: 280 },
      style: themeStyles.metricNode,
    },
    {
      id: 'security-tests',
      data: { label: '🧪 Security Tests\n❌ 0 penetration tests\n❌ No OWASP checks' },
      position: { x: 1025, y: 380 },
      style: themeStyles.suggestionNode,
    },
    {
      id: 'compliance-score',
      data: { label: '📋 Compliance\n❌ GDPR violations\n❌ No audit logs' },
      position: { x: 1175, y: 380 },
      style: themeStyles.suggestionNode,
    },
    
    // Genetic Algorithm Engine
    {
      id: 'genetic-algo',
      data: { label: '🧬 Security Evolution Engine\n\n🔄 Mutation: Add security layers & validations\n🔗 Crossover: Combine defense strategies\n📈 Selection: Optimize for security + performance\n🎲 Population: 20 hardened variants' },
      position: { x: 100, y: 500 },
      style: { ...themeStyles.uniqueFeatureNode, width: 300, height: 140 },
    },
    
    // AI Agents with Prompts
    {
      id: 'ai-agents',
      data: { label: '🤖 Security Specialist Agents' },
      position: { x: 500, y: 500 },
      style: { ...themeStyles.analysisNode, width: 260 },
    },
    {
      id: 'injection-agent',
      data: { label: '💉 Injection Defense Agent\n\n📝 Prompt:\n"Prevent all injection attacks.\nUse parameterized queries,\ninput validation, and sanitization.\nImplement WAF rules for\nSQL, NoSQL, and XSS protection."' },
      position: { x: 200, y: 600 },
      style: { ...themeStyles.llmNode, width: 220, height: 160 },
    },
    {
      id: 'auth-agent',
      data: { label: '🔐 Authentication Agent\n\n📝 Prompt:\n"Implement secure authentication:\nBcrypt password hashing, JWT tokens,\nmulti-factor authentication,\nsession management, and\nOAuth2 integration."' },
      position: { x: 500, y: 600 },
      style: { ...themeStyles.llmNode, width: 220, height: 160 },
    },
    {
      id: 'encryption-agent',
      data: { label: '🔒 Encryption Agent\n\n📝 Prompt:\n"Add encryption at rest and in transit.\nImplement AES-256, HTTPS enforcement,\ndata masking, key rotation,\nand secure data transmission."' },
      position: { x: 800, y: 600 },
      style: { ...themeStyles.llmNode, width: 220, height: 160 },
    },
    
    // Generation 1 Label
    {
      id: 'gen1-label',
      data: { label: '🌱 Generation 1: Initial Security Hardening (Population: 20)' },
      position: { x: 500, y: 800 },
      style: { ...themeStyles.metricNode, width: 400 },
    },
    
    // Generation 1 Variants
    {
      id: 'gen1-1',
      type: 'collapsibleCode',
      data: { 
        filename: 'secure-auth-v1a.js (Injection Protection)',
        code: '// SQL injection prevention variant\n' +
              '+ app.post("/login", async (req, res) => {\n' +
              '+   const { username, password } = req.body;\n' +
              '+   \n' +
              '+   // Input validation\n' +
              '+   if (!validateInput(username, password)) {\n' +
              '+     return res.status(400).json({ error: "Invalid input" });\n' +
              '+   }\n' +
              '+   \n' +
              '+   try {\n' +
              '+     // Parameterized query prevents SQL injection\n' +
              '+     const query = "SELECT id, username, password_hash FROM users WHERE username = ?";\n' +
              '+     const [rows] = await db.execute(query, [username]);\n' +
              '+     \n' +
              '+     if (rows.length > 0) {\n' +
              '+       const isValid = await bcrypt.compare(password, rows[0].password_hash);\n' +
              '+       if (isValid) {\n' +
              '+         res.json({ success: true, userId: rows[0].id });\n' +
              '+       }\n' +
              '+     }\n' +
              '+   } catch (error) {\n' +
              '+     logSecurityEvent("login_attempt", { username, ip: req.ip });\n' +
              '+     res.status(500).json({ error: "Authentication failed" });\n' +
              '+   }\n' +
              '+ });',
        score: 3.5
      },
      position: { x: 50, y: 900 },
    },
    {
      id: 'gen1-2',
      type: 'collapsibleCode',
      data: { 
        filename: 'secure-auth-v1b.js (Auth Enhancement)',
        code: '// JWT and session management variant\n' +
              '+ const jwt = require("jsonwebtoken");\n' +
              '+ const rateLimit = require("express-rate-limit");\n' +
              '+\n' +
              '+ const loginLimiter = rateLimit({\n' +
              '+   windowMs: 15 * 60 * 1000, // 15 minutes\n' +
              '+   max: 5, // limit each IP to 5 requests per windowMs\n' +
              '+   message: "Too many login attempts"\n' +
              '+ });\n' +
              '+\n' +
              '+ app.post("/login", loginLimiter, async (req, res) => {\n' +
              '+   const { username, password } = req.body;\n' +
              '+   \n' +
              '+   const user = await authenticateUser(username, password);\n' +
              '+   if (user) {\n' +
              '+     const token = jwt.sign(\n' +
              '+       { userId: user.id, username: user.username },\n' +
              '+       process.env.JWT_SECRET,\n' +
              '+       { expiresIn: "1h" }\n' +
              '+     );\n' +
              '+     \n' +
              '+     res.json({ success: true, token });\n' +
              '+   } else {\n' +
              '+     await new Promise(resolve => setTimeout(resolve, 1000)); // Timing attack prevention\n' +
              '+     res.status(401).json({ success: false });\n' +
              '+   }\n' +
              '+ });',
        score: 4.2
      },
      position: { x: 400, y: 900 },
    },
    {
      id: 'gen1-3',
      type: 'collapsibleCode',
      data: { 
        filename: 'secure-auth-v1c.js (Encryption Focus)',
        code: '// Data encryption and HTTPS variant\n' +
              '+ const crypto = require("crypto");\n' +
              '+ const helmet = require("helmet");\n' +
              '+\n' +
              '+ app.use(helmet()); // Security headers\n' +
              '+ app.use(express.json({ limit: "10mb" }));\n' +
              '+\n' +
              '+ // Encrypt sensitive data\n' +
              '+ function encryptSensitiveData(data) {\n' +
              '+   const cipher = crypto.createCipher("aes-256-cbc", process.env.ENCRYPTION_KEY);\n' +
              '+   let encrypted = cipher.update(data, "utf8", "hex");\n' +
              '+   encrypted += cipher.final("hex");\n' +
              '+   return encrypted;\n' +
              '+ }\n' +
              '+\n' +
              '+ app.post("/login", async (req, res) => {\n' +
              '+   const { username, password } = req.body;\n' +
              '+   \n' +
              '+   // Add CSRF protection\n' +
              '+   if (!req.headers["x-csrf-token"]) {\n' +
              '+     return res.status(403).json({ error: "CSRF token required" });\n' +
              '+   }\n' +
              '+   \n' +
              '+   const user = await secureAuthenticate(username, password);\n' +
              '+   if (user) {\n' +
              '+     // Encrypt response data\n' +
              '+     const encryptedResponse = encryptSensitiveData(JSON.stringify(user));\n' +
              '+     res.json({ success: true, data: encryptedResponse });\n' +
              '+   }\n' +
              '+ });',
        score: 4.0
      },
      position: { x: 750, y: 900 },
    },
    {
      id: 'gen1-more',
      data: { label: '... 17 more variants\nwith different defenses' },
      position: { x: 950, y: 920 },
      style: { ...themeStyles.suggestionNode, width: 150 },
    },
    
    // Evolution Process
    {
      id: 'evolution-1',
      type: 'evolution',
      data: { 
        generation: '1→2',
        description: '🔄 Defense in Depth Strategy',
        scores: {
          'Vulnerabilities': '12 → 4',
          'Auth Security': '1 → 7',
          'Data Protection': '2 → 6',
          'Attack Resistance': '+300%'
        }
      },
      position: { x: 400, y: 1100 },
    },
    
    // Generation 2 Label
    {
      id: 'gen2-label',
      data: { label: '🧬 Generation 2: Multi-Layer Security (Defense in Depth)' },
      position: { x: 500, y: 1250 },
      style: { ...themeStyles.metricNode, width: 450 },
    },
    
    // Generation 2 - Combined Security
    {
      id: 'gen2-1',
      type: 'collapsibleCode',
      data: { 
        filename: 'secure-api-system.js (Elite Security)',
        code: '// Combined multi-layer security system\n' +
              '+ import { SecurityMiddleware } from "./security/SecurityMiddleware";\n' +
              '+ import { AuthService } from "./auth/AuthService";\n' +
              '+ import { EncryptionService } from "./crypto/EncryptionService";\n' +
              '+ import { AuditLogger } from "./audit/AuditLogger";\n' +
              '+\n' +
              '+ class SecureAPI {\n' +
              '+   constructor() {\n' +
              '+     this.securityMiddleware = new SecurityMiddleware();\n' +
              '+     this.authService = new AuthService();\n' +
              '+     this.encryption = new EncryptionService();\n' +
              '+     this.auditLogger = new AuditLogger();\n' +
              '+   }\n' +
              '+\n' +
              '+   async handleLogin(req, res) {\n' +
              '+     const requestId = generateRequestId();\n' +
              '+     \n' +
              '+     try {\n' +
              '+       // Multi-layer validation\n' +
              '+       await this.securityMiddleware.validateRequest(req);\n' +
              '+       const credentials = await this.securityMiddleware.sanitizeInput(req.body);\n' +
              '+       \n' +
              '+       // Secure authentication with MFA\n' +
              '+       const authResult = await this.authService.authenticateUser(credentials);\n' +
              '+       \n' +
              '+       if (authResult.success) {\n' +
              '+         const encryptedToken = await this.encryption.generateSecureToken(authResult.user);\n' +
              '+         await this.auditLogger.logSuccessfulAuth(authResult.user, req.ip);\n' +
              '+         \n' +
              '+         res.json({ success: true, token: encryptedToken });\n' +
              '+       }\n' +
              '+     } catch (error) {\n' +
              '+       await this.auditLogger.logSecurityIncident(error, req, requestId);\n' +
              '+       res.status(401).json({ success: false });\n' +
              '+     }\n' +
              '+   }\n' +
              '+ }',
        score: 4.8
      },
      position: { x: 200, y: 1350 },
    },
    {
      id: 'gen2-2',
      type: 'collapsibleCode',
      data: { 
        filename: 'security-middleware.js (Defense Layer)',
        code: '// Comprehensive security middleware\n' +
              '+ export class SecurityMiddleware {\n' +
              '+   constructor() {\n' +
              '+     this.rateLimiter = new RateLimiter();\n' +
              '+     this.inputValidator = new InputValidator();\n' +
              '+     this.threatDetector = new ThreatDetector();\n' +
              '+   }\n' +
              '+\n' +
              '+   async validateRequest(req) {\n' +
              '+     // Rate limiting\n' +
              '+     await this.rateLimiter.checkLimit(req.ip);\n' +
              '+     \n' +
              '+     // Input validation and sanitization\n' +
              '+     this.inputValidator.validateHeaders(req.headers);\n' +
              '+     this.inputValidator.validateBody(req.body);\n' +
              '+     \n' +
              '+     // Threat detection\n' +
              '+     const threatLevel = await this.threatDetector.analyzeRequest(req);\n' +
              '+     if (threatLevel > THREAT_THRESHOLD) {\n' +
              '+       throw new SecurityThreatError("Suspicious activity detected");\n' +
              '+     }\n' +
              '+     \n' +
              '+     // OWASP security headers\n' +
              '+     this.setSecurityHeaders(req.res);\n' +
              '+   }\n' +
              '+\n' +
              '+   setSecurityHeaders(res) {\n' +
              '+     res.setHeader("X-Content-Type-Options", "nosniff");\n' +
              '+     res.setHeader("X-Frame-Options", "DENY");\n' +
              '+     res.setHeader("X-XSS-Protection", "1; mode=block");\n' +
              '+     res.setHeader("Strict-Transport-Security", "max-age=31536000");\n' +
              '+   }\n' +
              '+ }',
        score: 4.6
      },
      position: { x: 600, y: 1350 },
    },
    
    // Final Evolution
    {
      id: 'evolution-2',
      type: 'evolution',
      data: { 
        generation: '2→Final',
        description: '🏆 Enterprise Security Standard',
        scores: {
          'Vulnerabilities': '4→0',
          'Auth Security': '7→10',
          'Data Protection': '6→10',
          'Compliance': '100%'
        }
      },
      position: { x: 400, y: 1550 },
    },
    
    // Final Output
    {
      id: 'final-output',
      type: 'collapsibleCode',
      data: { 
        filename: 'Enterprise Security System',
        code: '// Final hardened security system\n' +
              '// ✅ Zero critical vulnerabilities\n' +
              '// ✅ OWASP Top 10 protection\n' +
              '// ✅ SOC 2 Type II compliant\n' +
              '// ✅ GDPR compliant with audit trails\n' +
              '// ✅ Multi-factor authentication\n' +
              '// ✅ End-to-end encryption\n' +
              '// ✅ Real-time threat monitoring\n' +
              '// ✅ Automated incident response\n' +
              '\n' +
              '🛡️ Security Architecture:\n' +
              '├── WAF (Web Application Firewall)\n' +
              '├── Rate limiting & DDoS protection\n' +
              '├── Input validation & sanitization\n' +
              '├── Authentication & authorization\n' +
              '├── Data encryption (AES-256)\n' +
              '├── Security monitoring & SIEM\n' +
              '└── Automated vulnerability scanning\n' +
              '\n' +
              '// Security metrics:\n' +
              '// • 0 critical vulnerabilities\n' +
              '// • 99.9% attack prevention rate\n' +
              '// • <100ms security overhead\n' +
              '// • 24/7 threat monitoring',
        score: 4.9
      },
      position: { x: 400, y: 1700 },
    },
    
    // Benefits Box
    {
      id: 'benefits',
      data: { label: '✨ Security Benefits\n\n• 100% critical vuln elimination\n• 1000% attack resistance increase\n• Full regulatory compliance\n• Real-time threat protection\n• Automated security evolution' },
      position: { x: 800, y: 1650 },
      style: { ...themeStyles.uniqueFeatureNode, width: 250, height: 140 },
    }
  ];
};

const securityEdges = [
  // Initial flow
  { id: 'e1', source: 'user-intent', target: 'security-analysis', animated: true, style: themeStyles.edgeStyle },
  { id: 'e2', source: 'security-analysis', target: 'initial-code', style: themeStyles.edgeStyle },
  { id: 'e3', source: 'initial-code', target: 'scoring-label', animated: true, style: themeStyles.edgeStyle },
  
  // Scoring connections
  { id: 'e4', source: 'scoring-label', target: 'vulnerability-score', style: themeStyles.edgeStyle },
  { id: 'e5', source: 'scoring-label', target: 'authentication-score', style: themeStyles.edgeStyle },
  { id: 'e6', source: 'scoring-label', target: 'data-protection-score', style: themeStyles.edgeStyle },
  { id: 'e7', source: 'scoring-label', target: 'security-tests', style: themeStyles.edgeStyle },
  { id: 'e8', source: 'scoring-label', target: 'compliance-score', style: themeStyles.edgeStyle },
  
  // Genetic Algorithm
  { id: 'e9', source: 'initial-code', target: 'genetic-algo', style: themeStyles.evolutionEdgeStyle },
  { id: 'e10', source: 'genetic-algo', target: 'ai-agents', animated: true, style: themeStyles.evolutionEdgeStyle },
  
  // AI Agents
  { id: 'e11', source: 'ai-agents', target: 'injection-agent', style: themeStyles.edgeStyle },
  { id: 'e12', source: 'ai-agents', target: 'auth-agent', style: themeStyles.edgeStyle },
  { id: 'e13', source: 'ai-agents', target: 'encryption-agent', style: themeStyles.edgeStyle },
  
  // Generation 1
  { id: 'e14', source: 'injection-agent', target: 'gen1-1', style: themeStyles.edgeStyle },
  { id: 'e15', source: 'auth-agent', target: 'gen1-2', style: themeStyles.edgeStyle },
  { id: 'e16', source: 'encryption-agent', target: 'gen1-3', style: themeStyles.edgeStyle },
  { id: 'e17', source: 'ai-agents', target: 'gen1-more', style: { ...themeStyles.edgeStyle, strokeDasharray: '5 5' } },
  
  // Evolution to Gen 1
  { id: 'e18', source: 'gen1-1', target: 'evolution-1', style: themeStyles.evolutionEdgeStyle },
  { id: 'e19', source: 'gen1-2', target: 'evolution-1', animated: true, style: themeStyles.evolutionEdgeStyle },
  { id: 'e20', source: 'gen1-3', target: 'evolution-1', style: themeStyles.evolutionEdgeStyle },
  
  // Feedback loops
  { id: 'f1', source: 'vulnerability-score', target: 'evolution-1', style: themeStyles.feedbackEdgeStyle, type: 'smoothstep' },
  { id: 'f2', source: 'security-tests', target: 'evolution-1', style: themeStyles.feedbackEdgeStyle, type: 'smoothstep' },
  { id: 'f3', source: 'compliance-score', target: 'evolution-1', style: themeStyles.feedbackEdgeStyle, type: 'smoothstep' },
  
  // Generation 2
  { id: 'e21', source: 'evolution-1', target: 'gen2-1', animated: true, style: themeStyles.evolutionEdgeStyle },
  { id: 'e22', source: 'evolution-1', target: 'gen2-2', animated: true, style: themeStyles.evolutionEdgeStyle },
  
  // Final Evolution
  { id: 'e23', source: 'gen2-1', target: 'evolution-2', style: themeStyles.evolutionEdgeStyle },
  { id: 'e24', source: 'gen2-2', target: 'evolution-2', style: themeStyles.evolutionEdgeStyle },
  
  // Final Output
  { id: 'e25', source: 'evolution-2', target: 'final-output', animated: true, style: themeStyles.evolutionEdgeStyle },
  { id: 'e26', source: 'final-output', target: 'benefits', style: themeStyles.edgeStyle },
];

const ArtemisSecurityHardening = () => {
  const [nodes, setNodes] = useState(getInitialNodes());
  const [edges, setEdges] = useState(securityEdges);

  // Add custom styles on mount
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = customStyles;
    document.head.appendChild(styleSheet);
    
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  // Save positions whenever nodes change
  const onNodesChange = useCallback((changes) => {
    setNodes((nds) => {
      const updatedNodes = applyNodeChanges(changes, nds);
      const positions = updatedNodes.map(node => ({
        ...node,
        style: node.style,
        type: node.type,
        data: node.data
      }));
      localStorage.setItem('artemisSecurityHardeningPositions', JSON.stringify(positions));
      return updatedNodes;
    });
  }, []);

  const onEdgesChange = useCallback((changes) => {
    setEdges((eds) => applyEdgeChanges(changes, eds));
  }, []);

  // Reset positions
  const resetPositions = () => {
    localStorage.removeItem('artemisSecurityHardeningPositions');
    setNodes(getInitialNodes());
  };

  return (
    <ReactFlowProvider>
      <div style={{ 
        height: '100vh', 
        width: '100%', 
        background: 'linear-gradient(135deg, #F0F4F8 0%, #D9E2EC 100%)',
        position: 'relative'
      }}>
        <h2 style={{ 
          textAlign: 'center', 
          color: '#102A43', 
          padding: '20px', 
          margin: '0',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
          fontSize: '26px',
          fontWeight: '700'
        }}>
          🔒 Artemis Security Hardening - Evolving Protection
        </h2>
        
        <div style={{
          position: 'absolute',
          top: '70px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '8px 20px',
          background: 'rgba(16, 42, 67, 0.08)',
          borderRadius: '20px',
          color: '#486581',
          fontSize: '14px',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
          zIndex: 100
        }}>
          Genetic algorithms evolve multi-layer security defenses and vulnerability elimination
        </div>
        
        <button
          onClick={resetPositions}
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            padding: '8px 16px',
            background: '#F4511E',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '12px',
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
            zIndex: 100
          }}
        >
          Reset Layout
        </button>
        
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView
          style={{ height: 'calc(100vh - 120px)', marginTop: '120px' }}
        >
          <Background color="#aaa" gap={16} />
          <MiniMap 
            style={{
              background: 'white',
              border: '1px solid #ccc',
              borderRadius: '8px'
            }}
          />
          <Controls 
            style={{
              background: 'white',
              border: '1px solid #ccc',
              borderRadius: '8px'
            }}
          />
        </ReactFlow>
      </div>
    </ReactFlowProvider>
  );
};

export default ArtemisSecurityHardening; 